import { showToast } from '../components/Toast';

/** DynamoDB hard cap for one item (PK + SK + data). */
export const DYNAMO_ITEM_MAX_BYTES = 400 * 1024;
/** First in-app warning. Plenty of room left; time to notice. */
export const DYNAMO_ITEM_WARN_BYTES = 300 * 1024;
/** Stronger warning — saves can start failing soon. */
export const DYNAMO_ITEM_DANGER_BYTES = 360 * 1024;

const WARN_TOAST_COOLDOWN_MS = 15 * 60 * 1000;
const lastToastAt = new Map();

/**
 * localStorage key. Set to a number of KB (e.g. "1") to warn at that size
 * instead of 300 KB so a normal campaign can be used to test the toast.
 * Remove the key when you're done.
 */
export const DEBUG_ITEM_SIZE_WARN_KB_KEY = 'fokus.debugItemSizeWarnKb';

export function readDebugWarnBytes() {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(DEBUG_ITEM_SIZE_WARN_KB_KEY);
  const kb = Number(raw);
  if (!Number.isFinite(kb) || kb <= 0) return null;
  return Math.round(kb * 1024);
}

/** UTF-8 bytes of the item the save Lambda actually Puts. */
export function estimateSprintItemBytes(sprint) {
  if (!sprint) return 0;
  const item = {
    PK: 'WORKSPACE#00000000-0000-0000-0000-000000000000',
    SK: `SPRINT#${sprint.id || ''}`,
    data: sprint,
  };
  const json = JSON.stringify(item);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(json).length;
  return unescape(encodeURIComponent(json)).length;
}

export function formatItemKb(bytes) {
  return Math.round(bytes / 1024);
}

export function sprintItemSizeLevel(bytes, warnBytes = DYNAMO_ITEM_WARN_BYTES) {
  if (bytes >= DYNAMO_ITEM_MAX_BYTES) return 'over';
  if (bytes >= DYNAMO_ITEM_DANGER_BYTES) return 'danger';
  if (bytes >= warnBytes) return 'warn';
  return null;
}

export function sprintItemSizeMessage(sprint, bytes, level) {
  const name = sprint?.name ? `"${sprint.name}"` : 'This campaign';
  const used = formatItemKb(bytes);
  const cap = formatItemKb(DYNAMO_ITEM_MAX_BYTES);
  if (level === 'over') {
    return `${name} is about ${used} KB. DynamoDB's cap is ${cap} KB — saves may fail until this is smaller.`;
  }
  if (level === 'danger') {
    return `${name} is about ${used} KB of ${cap} KB. DynamoDB will refuse saves if it hits the cap.`;
  }
  return `${name} is getting large (about ${used} KB of ${cap} KB). DynamoDB items cannot exceed ${cap} KB.`;
}

/**
 * Toast once per campaign/level for a while so the 2s save loop does not spam.
 * Returns the level, or null if under the warning line.
 */
export function maybeWarnSprintItemSize(sprint) {
  if (!sprint || !Array.isArray(sprint.tasks)) return null;
  const bytes = estimateSprintItemBytes(sprint);
  const warnAt = readDebugWarnBytes() ?? DYNAMO_ITEM_WARN_BYTES;
  const level = sprintItemSizeLevel(bytes, warnAt);
  if (!level) return null;

  const key = `${sprint.id || 'unknown'}:${level}`;
  const now = Date.now();
  if ((lastToastAt.get(key) || 0) + WARN_TOAST_COOLDOWN_MS > now) return level;
  lastToastAt.set(key, now);

  showToast(sprintItemSizeMessage(sprint, bytes, level), {
    type: level === 'warn' ? 'info' : 'error',
    duration: 10000,
  });
  return level;
}

export function isDynamoItemSizeError(error) {
  const msg = `${error?.message || ''} ${error?.code || ''}`;
  return /item size|400\s*kb|too large|ValidationException/i.test(msg);
}

/** For tests — do not call from the app. */
export function _resetSprintItemSizeToasts() {
  lastToastAt.clear();
}
